import { Controller } from '@hotwired/stimulus';
import { buildJsonHeaders } from 'utils/csrf_helpers';

export default class extends Controller {
  static targets = [
    'flash',
    'searchInput',
    'searchResults',
    'searchContainer',
    'registrationList',
    'registrationEmpty',
    'checkedList',
    'checkedEmpty',
    'registeredCount',
    'checkedCount',
    'checkedHeadingCount',
    'awaitingCount',
    'noShowCount',
    'noShowHeadingCount',
    'toggleButton',
    'toggleStatus',
    'openOnlySection',
    'collectSheet',
    'collectSheetName',
    'collectSheetQr',
    'collectSheetQrDefault',
    'collectSheetBody',
    'collectSheetConfirmed',
    'collectSheetConfirmedTitle',
    'collectCheckButton',
    'regSheet',
    'regSheetName',
    'regSwitchButton',
    'regRemoveButton',
  ];

  static values = {
    eventId: Number,
    attendanceUrl: String,
    searchUrl: String,
    toggleUrl: String,
    paymentQrUrl: String,
    paymentStatusUrl: String,
    // Payment poll: ~1s while they're most likely paying so the checkmark
    // lands right after their phone says paid, then ~4s to spare the server
    pollInterval: { type: Number, default: 1000 },
    slowPollInterval: { type: Number, default: 4000 },
  };

  connect() {
    this.flashTimeout = null;
    this.searchTimeout = null;
    this.collectPollTimer = null;
    this.collectAutoCloseTimer = null;
    this.collectAttendanceId = null;
  }

  disconnect() {
    if (this.flashTimeout) clearTimeout(this.flashTimeout);
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.stopCollectPolling();
    if (this.collectAutoCloseTimer) clearTimeout(this.collectAutoCloseTimer);
    document.body.classList.remove('mci-sheet-open');
  }

  // Defense-in-depth for state-changing taps: if the controller is ever
  // double-registered (e.g. two pack instances after a mid-session deploy —
  // the double check-in bug), both handlers receive the SAME event object.
  // The first claims it; the second bails. One tap, one action, always.
  claimEvent(event) {
    if (event.__mciClaimed) return false;
    event.__mciClaimed = true;
    return true;
  }

  // --- Check In ---

  async checkIn(event) {
    if (!this.claimEvent(event)) return;
    const button = event.currentTarget;
    const card = button.closest('[data-registration-id]');
    if (!card) return;

    const userId = card.dataset.userId;
    if (!userId) {
      this.showMessage('Unable to check in — no linked account.', 'error');
      return;
    }

    const userName = card.dataset.userName;
    // Same as desktop: a virtual registrant checks in as a virtual attendance
    const isVirtual = card.dataset.virtual === 'true';
    button.disabled = true;
    button.textContent = 'Checking in…';

    try {
      const response = await fetch(this.attendanceUrlValue, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          attendance: {
            user_id: userId,
            event_id: this.eventIdValue,
            virtual: isVirtual,
          },
        }),
      });

      if (!response.ok) {
        const error = await this.extractError(response);
        throw new Error(error);
      }

      let attendance = {};
      try {
        attendance = await response.json();
      } catch (_) {
        // non-JSON response is okay
      }

      // Archive the registration so it doesn't reappear on reload
      const registrationId = card.dataset.registrationId;
      await this.archiveRegistration(registrationId);

      // Animate out and remove from awaiting
      await this.animateOut(card);
      card.remove();
      this.toggleEmpty(
        this.registrationListTarget,
        this.registrationEmptyTarget
      );

      // Build and prepend checked-in card with credit info from response
      const checkedCard = this.buildCheckedCard(
        attendance,
        userName,
        userId,
        isVirtual
      );
      if (this.hasCheckedListTarget) {
        this.checkedListTarget.prepend(checkedCard);
      }
      this.toggleEmpty(this.checkedListTarget, this.checkedEmptyTarget);

      this.updateCount(this.awaitingCountTarget, -1);
      this.updateCount(this.registeredCountTarget, -1);
      this.updateCount(this.checkedCountTarget, 1);
      this.updateCount(this.checkedHeadingCountTarget, 1);

      const creditsMsg = this.formatCreditsMessage(attendance);
      this.showMessage(`${userName} is checked in.${creditsMsg}`, 'success');
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Check in';
      this.showMessage(
        error.message || 'Unable to check in. Please try again.',
        'error'
      );
    }
  }

  // --- Mark No-Show ---

  async markNoShow(event) {
    if (!this.claimEvent(event)) return;
    const button = event.currentTarget;
    const card = button.closest('[data-registration-id]');
    if (!card) return;

    const userId = card.dataset.userId;
    if (!userId) {
      this.showMessage('Unable to mark no-show — no linked account.', 'error');
      return;
    }

    const userName = card.dataset.userName;

    if (!confirm(`Mark ${userName} as a no-show?`)) return;

    button.disabled = true;
    button.textContent = 'Marking…';

    try {
      const response = await fetch(this.attendanceUrlValue, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          attendance: {
            user_id: userId,
            event_id: this.eventIdValue,
            no_show: true,
            // Same as desktop: virtual registrants no-show as virtual
            virtual: card.dataset.virtual === 'true',
          },
        }),
      });

      if (!response.ok) {
        const error = await this.extractError(response);
        throw new Error(error);
      }

      // Archive the registration
      const registrationId = card.dataset.registrationId;
      await this.archiveRegistration(registrationId);

      // Remove from awaiting list
      await this.animateOut(card);
      card.remove();
      this.toggleEmpty(
        this.registrationListTarget,
        this.registrationEmptyTarget
      );

      this.updateCount(this.awaitingCountTarget, -1);
      this.updateCount(this.registeredCountTarget, -1);

      this.showMessage(`${userName} marked as no-show.`, 'success');
    } catch (error) {
      button.disabled = false;
      button.textContent = 'No-show';
      this.showMessage(
        error.message || 'Unable to mark no-show. Please try again.',
        'error'
      );
    }
  }

  // --- Check In from Search ---

  async checkInFromSearch(event) {
    if (!this.claimEvent(event)) return;
    const button = event.currentTarget;
    const userId = button.dataset.userId;
    const userName = button.dataset.userName;

    if (!userId) return;

    button.disabled = true;
    button.textContent = 'Checking in…';

    try {
      const response = await fetch(this.attendanceUrlValue, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          attendance: {
            user_id: userId,
            event_id: this.eventIdValue,
            virtual: false,
          },
        }),
      });

      if (!response.ok) {
        const error = await this.extractError(response);
        throw new Error(error);
      }

      let attendance = {};
      try {
        attendance = await response.json();
      } catch (_) {}

      // Remove and archive any matching registration card
      const regCard = this.registrationListTarget?.querySelector(
        `[data-user-id="${userId}"]`
      );
      if (regCard) {
        const registrationId = regCard.dataset.registrationId;
        await this.archiveRegistration(registrationId);
        await this.animateOut(regCard);
        regCard.remove();
        this.toggleEmpty(
          this.registrationListTarget,
          this.registrationEmptyTarget
        );
        this.updateCount(this.awaitingCountTarget, -1);
        this.updateCount(this.registeredCountTarget, -1);
      }

      // Add checked-in card with credit info
      const checkedCard = this.buildCheckedCard(attendance, userName, userId);
      if (this.hasCheckedListTarget) {
        this.checkedListTarget.prepend(checkedCard);
      }
      this.toggleEmpty(this.checkedListTarget, this.checkedEmptyTarget);
      this.updateCount(this.checkedCountTarget, 1);
      this.updateCount(this.checkedHeadingCountTarget, 1);

      // Clear search
      this.clearSearch();

      const creditsMsg = this.formatCreditsMessage(attendance);
      this.showMessage(`${userName} is checked in.${creditsMsg}`, 'success');
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Check in';
      this.showMessage(
        error.message || 'Unable to check in. Please try again.',
        'error'
      );
    }
  }

  // --- Undo Check-In ---

  async undoCheckIn(event) {
    if (!this.claimEvent(event)) return;
    const button = event.currentTarget;
    const attendanceId = button.dataset.attendanceId;
    const card = button.closest('.mci-card');
    if (!attendanceId || !card) return;

    const userName = card.dataset.userName;

    if (!confirm(`Undo check-in for ${userName}?`)) return;

    button.disabled = true;
    button.textContent = 'Undoing…';

    try {
      // Use .json extension to ensure Rails routes to JSON format
      const response = await fetch(
        `${this.attendanceUrlValue}/${attendanceId}.json`,
        {
          method: 'DELETE',
          headers: buildJsonHeaders(),
        }
      );

      // 200-299 range means success; 204 No Content is the expected response
      if (!response.ok) {
        const error = await this.extractError(response);
        throw new Error(error);
      }

      await this.animateOut(card);
      card.remove();
      this.toggleEmpty(this.checkedListTarget, this.checkedEmptyTarget);

      this.updateCount(this.checkedCountTarget, -1);
      this.updateCount(this.checkedHeadingCountTarget, -1);

      this.showMessage(`${userName} check-in has been undone.`, 'success');
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Undo';
      this.showMessage(
        error.message || 'Unable to undo. Please try again.',
        'error'
      );
    }
  }

  // --- Open/Close Class ---

  async toggleClass(event) {
    const button = event.currentTarget;
    const action = button.dataset.classAction; // 'open' or 'close'
    const label =
      action === 'close' ? 'Close this class?' : 'Re-open this class?';

    if (!confirm(label)) return;

    button.disabled = true;
    button.textContent = action === 'close' ? 'Closing…' : 'Re-opening…';

    try {
      const response = await fetch(this.toggleUrlValue, {
        method: 'PATCH',
        headers: buildJsonHeaders(),
      });

      if (!response.ok) {
        const error = await this.extractError(response);
        throw new Error(error);
      }

      const data = await response.json();
      this.showMessage(data.message, 'success');

      // Update button state
      if (data.is_open) {
        button.textContent = 'Close';
        button.dataset.classAction = 'close';
        button.className = 'mci-top-bar__action mci-top-bar__action--danger';
        if (this.hasToggleStatusTarget) {
          this.toggleStatusTarget.textContent = 'Open';
          this.toggleStatusTarget.className = 'mci-badge mci-badge--green';
        }
        // Show open-only sections (search, check-in buttons)
        this.openOnlySectionTargets.forEach(el => (el.hidden = false));
      } else {
        button.textContent = 'Re-open';
        button.dataset.classAction = 'open';
        button.className = 'mci-top-bar__action';
        if (this.hasToggleStatusTarget) {
          this.toggleStatusTarget.textContent = 'Closed';
          this.toggleStatusTarget.className = 'mci-badge mci-badge--muted';
        }
        // Hide open-only sections
        this.openOnlySectionTargets.forEach(el => (el.hidden = true));
      }

      button.disabled = false;
    } catch (error) {
      button.disabled = false;
      button.textContent = action === 'close' ? 'Close' : 'Re-open';
      this.showMessage(
        error.message || 'Unable to update class. Please try again.',
        'error'
      );
    }
  }

  // --- Collect Payment (in-page QR bottom sheet) ---

  openCollect(event) {
    // Without a sheet on the page, let the chip act as the plain link it is
    if (!this.hasCollectSheetTarget) return;
    event.preventDefault();

    const userName = event.currentTarget.dataset.userName;
    const userId = event.currentTarget.dataset.userId;
    const attendanceId = event.currentTarget.closest('[data-attendance-id]')
      ?.dataset?.attendanceId;
    if (this.hasCollectSheetNameTarget) {
      this.collectSheetNameTarget.textContent = userName
        ? `${userName} — scan with their phone camera`
        : 'Scan with their phone camera';
    }

    // Show the anonymous event QR immediately, then swap in the personalized
    // (tokened) one — collecting payment is for THEM, so their purchase
    // should charge and match to their account even if they're logged out.
    // Setting personalizeQrFor unconditionally invalidates any still-in-
    // flight QR response from a previous open (even a userless one).
    this.personalizeQrFor = userId || null;
    this.resetCollectQr();
    if (this.hasCollectSheetBodyTarget)
      this.collectSheetBodyTarget.hidden = false;
    if (this.hasCollectSheetConfirmedTarget)
      this.collectSheetConfirmedTarget.hidden = true;
    this.collectSheetTarget.hidden = false;
    document.body.classList.add('mci-sheet-open');

    if (userId) this.personalizeCollectQr(userId);

    // With a known attendance, watch for the payment to land — same status
    // poll as the kiosk pay sheet, same confirmation moment.
    this.collectUserName = userName || null;
    this.collectAttendanceId = attendanceId || null;
    if (this.hasCollectCheckButtonTarget) {
      this.collectCheckButtonTarget.hidden = !this.collectAttendanceId;
    }
    this.startCollectPolling();
  }

  // --- Collect payment confirmation (poll while the sheet is open) ---

  startCollectPolling() {
    this.stopCollectPolling();
    if (!this.paymentStatusUrlValue || !this.collectAttendanceId) return;

    // Fast for the first 2 minutes, then slow, capped at 6 minutes. Each
    // tick waits for the previous check, so requests never pile up.
    const run = {};
    const startedAt = Date.now();
    this.collectPollRun = run;

    const tick = async () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed >= 6 * 60 * 1000) {
        this.stopCollectPolling();
        return;
      }

      const paid = await this.checkCollectPaymentStatus();
      if (paid || this.collectPollRun !== run) return;

      const delay =
        elapsed < 2 * 60 * 1000
          ? this.pollIntervalValue
          : this.slowPollIntervalValue;
      this.collectPollTimer = setTimeout(tick, delay);
    };

    this.collectPollTimer = setTimeout(tick, this.pollIntervalValue);
  }

  stopCollectPolling() {
    this.collectPollRun = null;
    if (this.collectPollTimer) {
      clearTimeout(this.collectPollTimer);
      this.collectPollTimer = null;
    }
  }

  // Manual fallback: same check the poller runs, with visible feedback
  async checkCollectPaymentNow(event) {
    const button = event?.currentTarget;
    if (button) {
      button.disabled = true;
      button.textContent = 'Checking…';
    }

    const paid = await this.checkCollectPaymentStatus();

    if (button && !paid) {
      button.textContent = 'No payment yet — try again';
      setTimeout(() => {
        button.disabled = false;
        button.textContent = 'Check payment status';
      }, 2000);
    }
  }

  async checkCollectPaymentStatus() {
    if (!this.paymentStatusUrlValue || !this.collectAttendanceId) return false;

    try {
      const url = `${
        this.paymentStatusUrlValue
      }?attendance_id=${encodeURIComponent(this.collectAttendanceId)}`;
      const response = await fetch(url, { headers: buildJsonHeaders() });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.missing) {
        // Attendance was undone/no-showed mid-poll — nothing left to watch
        this.stopCollectPolling();
        return false;
      }
      if (response.ok && data.paid) {
        this.showCollectConfirmed();
        return true;
      }
    } catch (_) {
      // Poll again next tick; the manual button is the fallback
    }
    return false;
  }

  async showCollectConfirmed() {
    this.stopCollectPolling();

    if (this.hasCollectSheetConfirmedTitleTarget) {
      this.collectSheetConfirmedTitleTarget.textContent = this.collectUserName
        ? `Payment received — ${this.collectUserName}`
        : 'Payment received!';
    }
    if (this.hasCollectSheetBodyTarget)
      this.collectSheetBodyTarget.hidden = true;
    if (this.hasCollectSheetConfirmedTarget)
      this.collectSheetConfirmedTarget.hidden = false;

    if (this.collectAutoCloseTimer) clearTimeout(this.collectAutoCloseTimer);
    this.collectAutoCloseTimer = setTimeout(() => this.closeCollect(), 5000);

    // The attendance is settled — refresh the roster card behind the sheet
    // with the server-rendered truth (new credit count, the pass they just
    // bought, no Unpaid chip), instead of leaving pre-payment details up.
    const card = this.element.querySelector(
      `[data-attendance-id="${this.collectAttendanceId}"]`
    );
    if (!card) return;

    let refreshed = false;
    try {
      const response = await fetch(window.location.href, {
        headers: { Accept: 'text/html' },
      });
      if (response.ok) {
        const doc = new DOMParser().parseFromString(
          await response.text(),
          'text/html'
        );
        const fresh = doc.querySelector(
          `[data-attendance-id="${card.dataset.attendanceId}"]`
        );
        if (fresh) {
          card.replaceWith(fresh);
          refreshed = true;
        }
      }
    } catch (_) {
      // fall through to the minimal fix below
    }

    // Fallback if the refresh fetch failed: at least clear the Unpaid chip
    if (!refreshed) card.querySelector('.mci-card__collect')?.remove();
  }

  // --- Registration actions (⋯ on an awaiting card) ---

  openRegActions(event) {
    if (!this.hasRegSheetTarget) return;
    event.preventDefault();

    const card = event.currentTarget.closest('[data-registration-id]');
    if (!card) return;
    this.regCard = card;

    if (this.hasRegSheetNameTarget) {
      this.regSheetNameTarget.textContent = card.dataset.userName || '';
    }
    if (this.hasRegSwitchButtonTarget) {
      this.regSwitchButtonTarget.disabled = false;
      this.regSwitchButtonTarget.textContent =
        card.dataset.virtual === 'true'
          ? 'Switch to in-person'
          : 'Switch to virtual';
    }
    if (this.hasRegRemoveButtonTarget) {
      this.regRemoveButtonTarget.disabled = false;
      this.regRemoveButtonTarget.textContent = 'Remove registration';
    }

    this.regSheetTarget.hidden = false;
    document.body.classList.add('mci-sheet-open');
  }

  closeRegActions() {
    if (!this.hasRegSheetTarget) return;
    this.regCard = null;
    this.regSheetTarget.hidden = true;
    document.body.classList.remove('mci-sheet-open');
  }

  // Same call as desktop's mark-as-virtual/in-person: registrations#update
  async switchRegType(event) {
    if (!this.claimEvent(event)) return;
    const card = this.regCard;
    if (!card) return;

    const button = event.currentTarget;
    const toVirtual = card.dataset.virtual !== 'true';
    button.disabled = true;
    button.textContent = 'Switching…';

    try {
      const response = await fetch(
        `/registrations/${card.dataset.registrationId}.json`,
        {
          method: 'PUT',
          headers: buildJsonHeaders(),
          body: JSON.stringify({ registration: { virtual: toVirtual } }),
        }
      );
      if (!response.ok) throw new Error();

      card.dataset.virtual = String(toVirtual);
      let badge = card.querySelector('[data-role="virtual-badge"]');
      if (toVirtual && !badge) {
        badge = document.createElement('span');
        badge.className = 'mci-badge mci-badge--virtual';
        badge.dataset.role = 'virtual-badge';
        badge.textContent = 'Virtual';
        card.querySelector('.mci-card__body')?.appendChild(badge);
      } else if (!toVirtual && badge) {
        badge.remove();
      }

      this.closeRegActions();
      this.showMessage(
        `${card.dataset.userName} is now ${
          toVirtual ? 'virtual' : 'in-person'
        }.`,
        'success'
      );
    } catch (_) {
      button.disabled = false;
      button.textContent = toVirtual
        ? 'Switch to virtual'
        : 'Switch to in-person';
      this.showMessage(
        'Unable to update the registration. Please try again.',
        'error'
      );
    }
  }

  // Same call as desktop's remove: registrations#destroy (hard delete)
  async removeRegistration(event) {
    if (!this.claimEvent(event)) return;
    const card = this.regCard;
    if (!card) return;

    const userName = card.dataset.userName;
    if (!confirm(`Remove ${userName} from this class?`)) return;

    const button = event.currentTarget;
    button.disabled = true;
    button.textContent = 'Removing…';

    try {
      const response = await fetch(
        `/registrations/${card.dataset.registrationId}.json`,
        {
          method: 'DELETE',
          headers: buildJsonHeaders(),
        }
      );
      if (!response.ok) throw new Error();

      this.closeRegActions();
      await this.animateOut(card);
      card.remove();
      this.toggleEmpty(
        this.registrationListTarget,
        this.registrationEmptyTarget
      );
      this.updateCount(this.awaitingCountTarget, -1);
      this.updateCount(this.registeredCountTarget, -1);

      this.showMessage(`${userName} removed from this class.`, 'success');
    } catch (_) {
      button.disabled = false;
      button.textContent = 'Remove registration';
      this.showMessage(
        'Unable to remove the registration. Please try again.',
        'error'
      );
    }
  }

  resetCollectQr() {
    if (!this.hasCollectSheetQrTarget || !this.hasCollectSheetQrDefaultTarget)
      return;
    this.collectSheetQrTarget.innerHTML = this.collectSheetQrDefaultTarget.innerHTML;
  }

  async personalizeCollectQr(userId) {
    if (!this.paymentQrUrlValue || !this.hasCollectSheetQrTarget) return;

    this.personalizeQrFor = userId;
    try {
      const response = await fetch(this.paymentQrUrlValue, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.qr_svg) return; // default QR stays — still works

      // Ignore late responses if the sheet moved on to someone else
      if (this.personalizeQrFor !== userId) return;
      this.collectSheetQrTarget.innerHTML = data.qr_svg;
    } catch (_) {
      // default QR stays
    }
  }

  closeCollect() {
    if (!this.hasCollectSheetTarget) return;

    this.stopCollectPolling();
    if (this.collectAutoCloseTimer) {
      clearTimeout(this.collectAutoCloseTimer);
      this.collectAutoCloseTimer = null;
    }
    this.collectAttendanceId = null;

    this.collectSheetTarget.hidden = true;
    if (this.hasCollectSheetBodyTarget)
      this.collectSheetBodyTarget.hidden = false;
    if (this.hasCollectSheetConfirmedTarget)
      this.collectSheetConfirmedTarget.hidden = true;
    if (this.hasCollectCheckButtonTarget) {
      this.collectCheckButtonTarget.disabled = false;
      this.collectCheckButtonTarget.textContent = 'Check payment status';
    }
    document.body.classList.remove('mci-sheet-open');
  }

  // --- Search ---

  onSearch() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);

    const query = this.searchInputTarget.value.trim();
    if (query.length < 2) {
      this.hideSearchResults();
      return;
    }

    this.searchTimeout = setTimeout(() => this.performSearch(query), 250);
  }

  async performSearch(query) {
    try {
      const response = await fetch(
        `${this.searchUrlValue}?term=${encodeURIComponent(query)}`,
        {
          headers: { Accept: 'application/json' },
        }
      );

      if (!response.ok) return;

      const results = await response.json();
      this.renderSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
    }
  }

  renderSearchResults(results) {
    const container = this.searchResultsTarget;
    container.innerHTML = '';

    if (!results.length) {
      container.innerHTML =
        '<div class="mci-search__no-results">No match found</div>';
      container.hidden = false;
      return;
    }

    results.forEach(result => {
      const item = document.createElement('div');
      item.className = 'mci-search__item';
      item.innerHTML = `
        <span class="mci-search__item-name">${this.escapeHtml(
          result.label
        )}</span>
        <button
          class="mci-btn mci-btn--small mci-btn--primary"
          data-action="mobile-check-in#checkInFromSearch"
          data-user-id="${result.value}"
          data-user-name="${this.escapeHtml(result.label)}"
        >
          Check in
        </button>
      `;
      container.appendChild(item);
    });

    container.hidden = false;
  }

  hideSearchResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.hidden = true;
      this.searchResultsTarget.innerHTML = '';
    }
  }

  clearSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = '';
    }
    this.hideSearchResults();
  }

  // --- Registration Archiving ---

  async archiveRegistration(registrationId) {
    if (!registrationId) return;

    try {
      await fetch(`/registrations/${registrationId}/archive`, {
        method: 'PUT',
        headers: buildJsonHeaders(),
      });
    } catch (error) {
      console.error('Failed to archive registration', error);
    }
  }

  // --- UI Helpers ---

  formatCreditsMessage(attendance) {
    const credits = attendance?.user?.credits;
    if (credits == null) return '';

    const passes = attendance?.passes;
    let passInfo = '';
    if (passes && passes.length === 1 && passes[0].name) {
      passInfo = ` (${passes[0].name})`;
    } else if (passes && passes.length > 1) {
      passInfo = ` (${passes.length} passes)`;
    }

    return ` ${credits} credits remaining${passInfo}`;
  }

  buildCheckedCard(attendance, name, userId, isVirtual = false) {
    const card = document.createElement('div');
    card.className = 'mci-card mci-card--checked mci-card--incoming';
    card.dataset.attendanceId = attendance?.id || '';
    card.dataset.userName = name;

    const userPath = `/mobile-check-in/users/${userId}`;
    const credits = attendance?.user?.credits;
    const passes = attendance?.passes || [];

    let detailsHtml = '';
    if (credits != null) {
      const isNegative = String(credits).startsWith('-');
      detailsHtml += `<span class="mci-card__detail"><span class="mci-card__credits ${
        isNegative ? 'mci-card__credits--negative' : ''
      }">${this.escapeHtml(String(credits))} credits left</span></span>`;
    }
    if (passes.length === 1 && passes[0].name) {
      detailsHtml += `<span class="mci-card__detail mci-card__pass">${this.escapeHtml(
        passes[0].name
      )}`;
      if (passes[0].expiration_date) {
        const exp = new Date(passes[0].expiration_date);
        const expStr = exp.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
        detailsHtml += ` <span class="mci-card__exp">(exp ${expStr})</span>`;
      }
      detailsHtml += '</span>';
    } else if (passes.length > 1) {
      detailsHtml += `<span class="mci-card__detail mci-card__pass">${passes.length} passes used</span>`;
    } else if (passes.length === 0) {
      detailsHtml +=
        '<span class="mci-card__detail mci-card__pass mci-card__pass--none">No pass</span>';
    }

    // No pass consumed => the check-in is unpaid; offer to collect right away.
    // Link to the Collect page, upgraded by openCollect into the in-page sheet.
    let collectHtml = '';
    if (passes.length === 0) {
      collectHtml = `
        <div class="mci-card__collect">
          <span class="mci-badge mci-badge--red">Unpaid</span>
          <a href="/mobile-check-in/collect-payment?user_id=${encodeURIComponent(
            userId
          )}" class="mci-collect-chip" data-action="mobile-check-in#openCollect" data-user-name="${this.escapeHtml(
        name
      )}" data-user-id="${encodeURIComponent(
        userId
      )}">Collect payment &rsaquo;</a>
        </div>
      `;
    }

    const virtualHtml = isVirtual
      ? '<span class="mci-badge mci-badge--virtual">Virtual</span>'
      : '';

    card.innerHTML = `
      <div class="mci-card__body">
        <a href="${userPath}" class="mci-card__name mci-card__name--link">${this.escapeHtml(
      name
    )}</a>
        ${virtualHtml}
        <div class="mci-card__details">
          ${detailsHtml}
        </div>
        ${collectHtml}
      </div>
      <div class="mci-card__actions">
        <span class="mci-card__status mci-card__status--checked">Checked in</span>
        <button
          class="mci-btn mci-btn--small mci-btn--danger-outline"
          data-action="mobile-check-in#undoCheckIn"
          data-attendance-id="${attendance?.id || ''}"
        >
          Undo
        </button>
      </div>
    `;

    card.addEventListener(
      'animationend',
      () => card.classList.remove('mci-card--incoming'),
      { once: true }
    );

    return card;
  }

  animateOut(card) {
    return new Promise(resolve => {
      if (!card) return resolve();

      const done = () => {
        card.removeEventListener('animationend', done);
        resolve();
      };

      card.addEventListener('animationend', done);
      card.classList.add('mci-card--leaving');

      // Fallback in case animation doesn't fire
      setTimeout(done, 250);
    });
  }

  toggleEmpty(listTarget, emptyTarget) {
    if (!listTarget || !emptyTarget) return;

    if (listTarget.childElementCount === 0) {
      listTarget.hidden = true;
      emptyTarget.hidden = false;
    } else {
      listTarget.hidden = false;
      emptyTarget.hidden = true;
    }
  }

  updateCount(target, delta) {
    if (!target) return;
    const current = parseInt(target.textContent, 10) || 0;
    target.textContent = Math.max(0, current + delta);
  }

  showMessage(message, type = 'success') {
    if (!this.hasFlashTarget) return;

    const flash = this.flashTarget;
    flash.textContent = message;
    flash.hidden = false;
    flash.className = `mci-flash mci-flash--${type}`;

    if (this.flashTimeout) clearTimeout(this.flashTimeout);
    this.flashTimeout = setTimeout(() => {
      flash.hidden = true;
    }, 4000);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  async extractError(response) {
    try {
      const data = await response.json();
      if (data?.errors) {
        if (Array.isArray(data.errors)) return data.errors.join(', ');
        const base = data.errors.base || data.errors.full_messages;
        if (Array.isArray(base)) return base.join(', ');
      }
      if (data?.message) return data.message;
    } catch (_) {}

    return response.status === 422
      ? 'This person is already checked in.'
      : 'Unable to complete this action. Please try again.';
  }
}
